import { Quaternion, Vector3 } from 'three/webgpu';
import type { System } from '../core/Engine';
import type { Input } from '../core/Input';
import { settings } from '../core/settings';
import type { GravityField } from '../gravity/GravityField';
import { type PhysicsWorld, RAPIER } from '../physics/PhysicsWorld';

const CAPSULE_RADIUS = 0.35;
const CAPSULE_HALF = 0.55; // cylinder half-height: 1.8 m tall in total
export const CENTER_TO_EYE = 0.72; // eyes ~1.62 m above the feet
/** The gap the character controller keeps between the capsule and the ground (m). */
export const CONTROLLER_GAP = 0.03;
const WALK_SPEED = 4.6;
const SPRINT_SPEED = 9.5;
/**
 * A hero's jump: the same legs everywhere, so it clears v²/2g: 1.5 m on Titan (2.6 g),
 * about 2 m in the big worlds, more in small or weak-gravity ones.
 */
export const JUMP_SPEED = Math.sqrt(2 * 2.6 * 9.81 * 1.5);
/**
 * In the air without jump held, gravity pulls this many times harder (tap for a hop, hold
 * for a full jump, let go to drop). Only up to the speed a jump launches you at, so the
 * top speed of a fall stays gravity and drag's alone.
 */
export const FAST_FALL = 2.5;
const GROUND_ACCEL = 45;
const AIR_ACCEL = 5;
const FLOAT_ACCEL = 4;
const FLOAT_MAX_SPEED = 8;
/** Below this field strength (m/s²) the player floats instead of walking. */
const FLOAT_ENTER = 0.3;
const FLOAT_EXIT = 0.45;
/** Steepest walkable ground, as cos(angle) against up (~52°). */
const GROUND_COS = 0.6;
/** Quadratic air drag: terminal velocity ~55 m/s like a skydiver. */
const AIR_DRAG = 0.0032;
/**
 * Turning to match gravity goes at settings.reorientRate × |g| / REORIENT_G (at most
 * REORIENT_MAX times it): brisk on a world's ground, slow where gravity is weak (caves),
 * not at all where there is none.
 */
export const REORIENT_G = 6;
const REORIENT_MAX = 1.5;
/** Floating (no gravity to speak of), Q and E roll you round your aim at this rate (rad/s). */
export const ROLL_RATE = Math.PI / 2;

export type MoveMode = 'walk' | 'float' | 'fly';

const Y = new Vector3(0, 1, 0);
const _wish = new Vector3();
const _fwd = new Vector3();
const _right = new Vector3();
const _axis = new Vector3();
const _q = new Quaternion();
const _q2 = new Quaternion();
const _target = new Vector3();
const _local = new Vector3();
const _vh = new Vector3();
const _dv = new Vector3();
const _mouse = { x: 0, y: 0 };

/**
 * First-person controller for arbitrary, continuously changing gravity. The
 * body frame (+Y = up) turns toward −ĝ at a capped, comfortable rate; the
 * camera's yaw/pitch are relative to that frame, so heading is preserved while
 * "down" rotates. Collision uses Rapier's kinematic character controller with
 * its up vector updated every step.
 */
export class PlayerController implements System {
  readonly name = 'player';
  /** Universe-space capsule centre (f64). */
  readonly position = new Vector3();
  readonly velocity = new Vector3();
  readonly up = new Vector3(0, 1, 0);
  readonly body = new Quaternion();
  readonly gravity = new Vector3();
  yaw = 0;
  pitch = 0;
  grounded = false;
  mode: MoveMode = 'walk';
  flySpeed = 40;
  /** Set by the game; true while the player's input should drive movement. */
  controlsEnabled = true;
  /** Set by the world: true while what's around the player isn't there yet (a cave still being built). */
  frozen = false;
  /**
   * The fastest the view may turn (rad/s, yaw and pitch together). Mouse motion past it is
   * dropped, not saved up. Set by the game, e.g. while a weapon swings.
   */
  lookRateLimit = Number.POSITIVE_INFINITY;

  private readonly prevPosition = new Vector3();
  private readonly prevBody = new Quaternion();
  private readonly rb: RAPIER.RigidBody;
  /** The capsule (the third-person camera's casts leave it out). */
  readonly collider: RAPIER.Collider;
  private readonly kcc: RAPIER.KinematicCharacterController;
  private readonly probe = new RAPIER.Ball(0.3);

  constructor(
    private readonly physics: PhysicsWorld,
    private readonly field: GravityField,
    private readonly input: Input,
  ) {
    const world = physics.world;
    this.rb = world.createRigidBody(RAPIER.RigidBodyDesc.kinematicPositionBased());
    this.collider = world.createCollider(RAPIER.ColliderDesc.capsule(CAPSULE_HALF, CAPSULE_RADIUS), this.rb);
    this.kcc = world.createCharacterController(CONTROLLER_GAP);
    this.kcc.setMaxSlopeClimbAngle((52 * Math.PI) / 180);
    this.kcc.setMinSlopeSlideAngle((40 * Math.PI) / 180);
    this.kcc.enableAutostep(0.45, 0.25, false);
    this.kcc.enableSnapToGround(0.35);
    this.kcc.setApplyImpulsesToDynamicBodies(true);
    this.kcc.setSlideEnabled(true);
  }

  /** Place the player (feet on the ground below `position`) with the given up. */
  teleport(position: Vector3, up: Vector3): void {
    this.position.copy(position);
    this.prevPosition.copy(position);
    this.velocity.set(0, 0, 0);
    this.up.copy(up).normalize();
    this.body.setFromUnitVectors(Y, this.up);
    this.prevBody.copy(this.body);
    this.physics.rebase(position);
    this.physics.toLocal(position, _local);
    this.rb.setTranslation(_local, true);
    this.rb.setNextKinematicTranslation(_local);
    this.rb.setRotation(this.body, true);
  }

  /** Point the view at a universe position (yaw only). */
  faceTowards(target: Vector3): void {
    const to = new Vector3().subVectors(target, this.position);
    to.addScaledVector(this.up, -to.dot(this.up)).normalize();
    const f0 = new Vector3(0, 0, -1).applyQuaternion(this.body);
    this.yaw = Math.atan2(new Vector3().crossVectors(f0, to).dot(this.up), f0.dot(to));
    this.pitch = 0;
  }

  /** Render-rate mouse look. */
  frameUpdate(dt = 1 / 60): void {
    this.input.takeMouse(_mouse);
    if (!this.controlsEnabled) return;
    const s = 0.0022 * settings.mouseSensitivity;
    let dYaw = -_mouse.x * s;
    let dPitch = -_mouse.y * s * (settings.invertY ? -1 : 1);
    const turn = Math.hypot(dYaw, dPitch);
    const most = this.lookRateLimit * dt;
    if (turn > most) {
      dYaw *= most / turn;
      dPitch *= most / turn;
    }
    this.yaw += dYaw;
    this.pitch += dPitch;
    const lim = Math.PI / 2 - 0.01;
    this.pitch = Math.max(-lim, Math.min(lim, this.pitch));
    if (this.mode === 'fly') {
      const w = this.input.takeWheel();
      if (w !== 0) this.flySpeed = Math.min(20000, Math.max(2, this.flySpeed * 1.4 ** -w));
    }
  }

  fixedUpdate(dt: number): void {
    this.prevPosition.copy(this.position);
    this.prevBody.copy(this.body);
    if (this.frozen) {
      this.velocity.set(0, 0, 0);
      return;
    }
    if (this.physics.maybeRebase(this.position)) {
      this.physics.toLocal(this.position, _local);
      this.rb.setNextKinematicTranslation(_local);
    }

    const input = this.input;
    if (this.controlsEnabled && input.wasPressed('KeyV')) {
      this.mode = this.mode === 'fly' ? 'walk' : 'fly';
      this.velocity.set(0, 0, 0);
    }

    this.field.sample(this.position, this.gravity);
    const gMag = this.gravity.length();
    if (this.mode !== 'fly') {
      if (this.mode === 'walk' && gMag < FLOAT_ENTER) this.mode = 'float';
      else if (this.mode === 'float' && gMag > FLOAT_EXIT) this.mode = 'walk';
    }
    this.alignToGravity(gMag, dt);
    if (this.mode === 'float' && this.controlsEnabled) this.roll(dt);
    this.basis();

    // Input wish direction in the body's tangent plane (or 3D when floating/flying).
    _wish.set(0, 0, 0);
    if (this.controlsEnabled) {
      if (input.isDown('KeyW')) _wish.add(_fwd);
      if (input.isDown('KeyS')) _wish.sub(_fwd);
      if (input.isDown('KeyD')) _wish.add(_right);
      if (input.isDown('KeyA')) _wish.sub(_right);
    }

    if (this.mode === 'fly') {
      this.fly(dt);
      return;
    }

    if (this.mode === 'walk') {
      if (_wish.lengthSq() > 0) _wish.normalize();
      const speed = this.controlsEnabled && input.isDown('ShiftLeft') ? SPRINT_SPEED : WALK_SPEED;
      const vUp = this.velocity.dot(this.up);
      _vh.copy(this.velocity).addScaledVector(this.up, -vUp);
      _dv.copy(_wish).multiplyScalar(speed).sub(_vh);
      const maxDv = (this.grounded ? GROUND_ACCEL : AIR_ACCEL) * dt;
      if (_dv.length() > maxDv) _dv.setLength(maxDv);
      _vh.add(_dv);
      let newUp = vUp;
      if (this.grounded && this.controlsEnabled && input.wasPressed('Space')) {
        newUp = JUMP_SPEED;
        this.grounded = false;
      }
      this.velocity.copy(_vh).addScaledVector(this.up, newUp);
      this.velocity.addScaledVector(this.gravity, dt);
      if (!this.grounded) {
        // Not holding jump: fall faster, but only up to the speed a jump launches you at.
        const holding = this.controlsEnabled && input.isDown('Space');
        if (!holding && gMag > 0) {
          const vDown = this.velocity.dot(this.gravity) / gMag;
          if (vDown < JUMP_SPEED) {
            const extra = Math.min((FAST_FALL - 1) * gMag * dt, JUMP_SPEED - vDown);
            this.velocity.addScaledVector(this.gravity, extra / gMag);
          }
        }
        this.applyDrag(dt);
      }
    } else {
      // Floating: gentle levitation thrust along the view, Space/C for up/down.
      const pitchDir = _target
        .copy(_fwd)
        .multiplyScalar(Math.cos(this.pitch))
        .addScaledVector(this.up, Math.sin(this.pitch));
      _wish.set(0, 0, 0);
      if (this.controlsEnabled) {
        if (input.isDown('KeyW')) _wish.add(pitchDir);
        if (input.isDown('KeyS')) _wish.sub(pitchDir);
        if (input.isDown('KeyD')) _wish.add(_right);
        if (input.isDown('KeyA')) _wish.sub(_right);
        if (input.isDown('Space')) _wish.add(this.up);
        if (input.isDown('KeyC')) _wish.sub(this.up);
      }
      if (_wish.lengthSq() > 0) _wish.normalize();
      // Levitation thrust only up to a cruising speed along the wish direction.
      if (this.velocity.dot(_wish) < FLOAT_MAX_SPEED) this.velocity.addScaledVector(_wish, FLOAT_ACCEL * dt);
      this.velocity.addScaledVector(this.gravity, dt);
      this.velocity.multiplyScalar(1 - 0.25 * dt);
      this.applyDrag(dt);
    }

    this.move(dt);
  }

  private move(dt: number): void {
    const desired = _target.copy(this.velocity).multiplyScalar(dt);
    this.kcc.setUp(this.up);
    this.kcc.computeColliderMovement(this.collider, desired);
    const moved = this.kcc.computedMovement();
    // Rapier's own grounded flag accepts any contact up to ~90°, which makes shaft
    // walls count as floors; probe below the feet with our slope limit instead.
    const t0 = this.rb.translation();
    this.grounded = this.mode === 'walk' && this.probeGround(t0.x + moved.x, t0.y + moved.y, t0.z + moved.z);

    // Clip velocity against every contact so speed never accumulates into walls
    // (sliding along them keeps the tangential part). Rapier's normal1 points from the
    // character toward the obstacle; orient it against the attempted motion to be safe.
    const n = this.kcc.numComputedCollisions();
    for (let i = 0; i < n; i++) {
      const c = this.kcc.computedCollision(i);
      if (!c) continue;
      _dv.set(c.normal1.x, c.normal1.y, c.normal1.z);
      if (_dv.lengthSq() < 1e-8) continue;
      _dv.normalize();
      if (_dv.dot(desired) < 0) _dv.negate();
      const into = this.velocity.dot(_dv);
      if (into > 0) this.velocity.addScaledVector(_dv, -into);
    }
    if (this.grounded) {
      const vUp = this.velocity.dot(this.up);
      if (vUp < 0) this.velocity.addScaledVector(this.up, -vUp);
    }

    const t = this.rb.translation();
    _local.set(t.x + moved.x, t.y + moved.y, t.z + moved.z);
    this.rb.setNextKinematicTranslation(_local);
    this.rb.setNextKinematicRotation(this.body);
    this.physics.toUniverse(_local, this.position);
  }

  private applyDrag(dt: number): void {
    const v = this.velocity.length();
    if (v > 1e-3) this.velocity.multiplyScalar(Math.max(0, 1 - AIR_DRAG * v * dt));
  }

  /**
   * Sphere-cast along −up from the capsule centre. Starting at the centre (not the feet)
   * keeps tilted stances on slopes detected while walls beside the capsule are missed.
   */
  private probeGround(x: number, y: number, z: number): boolean {
    const up = this.up;
    const start = { x, y, z };
    const hit = this.physics.world.castShape(
      start,
      this.body,
      { x: -up.x, y: -up.y, z: -up.z },
      this.probe,
      0,
      CAPSULE_HALF + CAPSULE_RADIUS - 0.3 + 0.14,
      false,
      undefined,
      undefined,
      this.collider,
    );
    if (!hit) return false;
    // World.castShape gives the hit collider's normal in world space as normal1; normal2
    // is the probe's own, in its local frame (about −Y for any flat ground, so testing it
    // against `up` only worked where up happened to be near world ±Y).
    const n = hit.normal1;
    // Two-sided trimeshes report the normal per winding; only the slope matters here.
    return Math.abs(n.x * up.x + n.y * up.y + n.z * up.z) > GROUND_COS;
  }

  private fly(dt: number): void {
    const input = this.input;
    const dir = _target.set(0, 0, -1).applyQuaternion(this.viewQuaternion(this.body, _q2));
    _wish.set(0, 0, 0);
    if (this.controlsEnabled) {
      if (input.isDown('KeyW')) _wish.add(dir);
      if (input.isDown('KeyS')) _wish.sub(dir);
      if (input.isDown('KeyD')) _wish.add(_right);
      if (input.isDown('KeyA')) _wish.sub(_right);
      if (input.isDown('Space')) _wish.add(this.up);
      if (input.isDown('KeyC')) _wish.sub(this.up);
    }
    if (_wish.lengthSq() > 0) _wish.normalize();
    const boost = this.controlsEnabled && input.isDown('ShiftLeft') ? 6 : 1;
    this.velocity.copy(_wish).multiplyScalar(this.flySpeed * boost);
    this.position.addScaledVector(this.velocity, dt);
    this.physics.maybeRebase(this.position);
    this.physics.toLocal(this.position, _local);
    this.rb.setTranslation(_local, true);
    this.rb.setNextKinematicTranslation(_local);
    this.grounded = false;
  }

  /**
   * Turn the body toward −ĝ by at most the comfort rate, which scales with gravity's strength
   * (REORIENT_G): the weaker the pull, the slower "down" takes you round.
   */
  private alignToGravity(gMag: number, dt: number): void {
    if (gMag < 0.05) return;
    _target.copy(this.gravity).multiplyScalar(-1 / gMag);
    const angle = this.up.angleTo(_target);
    if (angle < 1e-5) return;
    let strength = Math.min(REORIENT_MAX, gMag / REORIENT_G);
    // Leaping across a cave passage, "down" swings to the far wall mid-air: turn twice as
    // fast, to land on your feet.
    if (!this.grounded && angle > Math.PI / 3) strength *= 2;
    const maxStep = ((settings.reorientRate * Math.PI) / 180) * strength * dt;
    const step = Math.min(angle, maxStep);
    _axis.crossVectors(this.up, _target);
    if (_axis.lengthSq() < 1e-12) _axis.set(1, 0, 0).applyQuaternion(this.body);
    _axis.normalize();
    _q.setFromAxisAngle(_axis, step);
    this.body.premultiply(_q).normalize();
    this.up.copy(Y).applyQuaternion(this.body);
  }

  /**
   * Floating: Q and E roll the body round the aim (the view's forward), so you can put any
   * wall under your feet. The aim stays where it is.
   */
  private roll(dt: number): void {
    const dir = (this.input.isDown('KeyE') ? 1 : 0) - (this.input.isDown('KeyQ') ? 1 : 0);
    if (dir === 0) return;
    // (viewQuaternion uses _axis itself: take the view first.)
    const view = this.viewQuaternion(this.body, _q2);
    _axis.set(0, 0, -1).applyQuaternion(view);
    // E rolls right (clockwise as you see it): a turn about the aim.
    _q.setFromAxisAngle(_axis, dir * ROLL_RATE * dt);
    this.body.premultiply(_q).normalize();
    this.up.copy(Y).applyQuaternion(this.body);
  }

  /** Forward/right in the body's tangent plane from the current yaw. */
  private basis(): void {
    _q.setFromAxisAngle(Y, this.yaw);
    _q2.copy(this.body).multiply(_q);
    _fwd.set(0, 0, -1).applyQuaternion(_q2);
    _right.set(1, 0, 0).applyQuaternion(_q2);
  }

  /** body * yaw * pitch */
  viewQuaternion(body: Quaternion, out: Quaternion): Quaternion {
    _q.setFromAxisAngle(Y, this.yaw);
    out.copy(body).multiply(_q);
    _q.setFromAxisAngle(_axis.set(1, 0, 0), this.pitch);
    return out.multiply(_q);
  }

  /** Body orientation between the last two physics steps (for drawing, `alpha` 0..1). */
  interpolatedBody(alpha: number, out: Quaternion): Quaternion {
    return out.slerpQuaternions(this.prevBody, this.body, alpha);
  }

  /** Capsule centre (universe) between the last two physics steps. */
  interpolatedPosition(alpha: number, out: Vector3): Vector3 {
    return out.lerpVectors(this.prevPosition, this.position, alpha);
  }

  /** Interpolated eye position (universe) and view orientation for rendering. */
  cameraPose(alpha: number, outPos: Vector3, outQuat: Quaternion): void {
    const bodyI = this.interpolatedBody(alpha, _q2);
    this.interpolatedPosition(alpha, outPos);
    const upI = _target.copy(Y).applyQuaternion(bodyI);
    outPos.addScaledVector(upI, CENTER_TO_EYE);
    this.viewQuaternion(bodyI, outQuat);
  }

  /** Feet position (universe) for HUD/altitude queries. */
  feet(out: Vector3): Vector3 {
    return out.copy(this.position).addScaledVector(this.up, -(CAPSULE_HALF + CAPSULE_RADIUS));
  }
}

export const PLAYER_CENTER_HEIGHT = CAPSULE_HALF + CAPSULE_RADIUS;
